import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

import { LoginPage } from '@/pages/LoginPage';
import { HomePage } from '@/pages/HomePage';
import { InicioPage } from '@/pages/InicioPage';
import { AgendaPage } from '@/pages/AgendaPage';
import { NumbersPage } from '@/pages/NumbersPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { PreCallPage } from '@/pages/PreCallPage';
import { ScreeningPage } from '@/pages/ScreeningPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { MfaEnrollPage } from '@/pages/MfaEnrollPage';
import { ClientProfilePage } from '@/pages/ClientProfilePage';
import { AppShell } from '@/components/layout/AppShell';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Toaster } from '@/components/ui/Toaster';
import { AudioUnlockProvider } from '@/components/AudioUnlockProvider';
import { ScrollToTop } from '@/components/ScrollToTop';
import { useAuthStore } from '@/stores/auth.store';

const AdminShell = lazy(() => import('@/pages/admin/AdminShell'));
const UploaderShell = lazy(() => import('@/pages/uploader/UploaderShell'));
const NavigatorShell = lazy(() => import('@/pages/navigator/NavigatorShell'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (count, err) => {
        if (err && typeof err === 'object' && 'status' in err) {
          const status = (err as { status: unknown }).status;
          if (typeof status === 'number' && status >= 400 && status < 500) return false;
        }
        return count < 2;
      },
    },
  },
});

function FullPageLoader(): JSX.Element {
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-subtle">
      <Loader2 className="h-6 w-6 text-lavender-400 animate-spin" aria-label="Loading" />
    </div>
  );
}

/**
 * Dispatch users from / to their role-appropriate landing.
 * BUG-2 (v0.4.1): admins/supervisors were seeing the agent HomePage with a
 *   "Mi cola" card that 403'd on click. They now redirect to /admin.
 *   Uploaders continue to redirect to /uploader (v0.4.0 behavior).
 * F5-R8a: el AGENTE gana pantalla de Inicio propia (dibujo «Agente v2 —
 *   Header»): saludo, Mi rango, Mi meta, lo que exige acción hoy y accesos.
 *   `/` ES Inicio — la marca del header lleva acá. HomePage queda solo como
 *   fallback de roles desconocidos.
 */
/**
 * F5-R9a: «Mi cola» se fundió DENTRO de «Inicio» — la ruta /queue NO muere:
 * los call-sites de PreCall/Screening siguen navegando acá y caen en Inicio.
 * CRÍTICO: se preserva el location.state — sin él muere el toast
 * justCompleted que ScreeningPage manda con navigate('/queue', {state}).
 */
function QueueRedirect(): JSX.Element {
  const loc = useLocation();
  return <Navigate to="/" replace state={loc.state} />;
}

function HomeOrRoleRedirect(): JSX.Element {
  const role = useAuthStore((s) => s.user?.role);
  if (role === 'admin' || role === 'supervisor') return <Navigate to="/admin" replace />;
  if (role === 'uploader') return <Navigate to="/uploader" replace />;
  if (role === 'navigator') return <Navigate to="/navigator" replace />;
  if (role === 'agent') return <InicioPage />;
  return <HomePage />;
}

export function App(): JSX.Element {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AudioUnlockProvider />
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/login" element={<LoginPage />} />

            {/* Navigator gets its own top-level shell too. Same reasoning as the
                uploader: AppShell's header is built for the agent/admin surface,
                and routing a role through it is how a menu ends up offering
                links that 403 on click. */}
            <Route
              path="/navigator/*"
              element={
                <ProtectedRoute roles={['navigator']}>
                  <Suspense fallback={<FullPageLoader />}>
                    <NavigatorShell />
                  </Suspense>
                </ProtectedRoute>
              }
            />

            {/* Uploader role gets its own top-level shell — separate from AppShell */}
            <Route
              path="/uploader/*"
              element={
                <ProtectedRoute roles={['uploader']}>
                  <Suspense fallback={<FullPageLoader />}>
                    <UploaderShell />
                  </Suspense>
                </ProtectedRoute>
              }
            />

            {/* F5-T3 (Admin 01): el admin gana chrome propio como navigator y
                uploader — /admin/* sale del AppShell del agente. */}
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute roles={['admin', 'supervisor']}>
                  <Suspense fallback={<FullPageLoader />}>
                    <AdminShell />
                  </Suspense>
                </ProtectedRoute>
              }
            />

            <Route
              element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<HomeOrRoleRedirect />} />
              {/* F5-R8a — pestañas del agente: /inicio es alias de / (el
                  logo y la pestaña llevan a /); agenda y números son las
                  superficies que se REPARTEN desde la QueuePage vieja. */}
              <Route path="/inicio" element={<Navigate to="/" replace />} />
              <Route path="/queue" element={<QueueRedirect />} />
              <Route
                path="/agenda"
                element={
                  <ProtectedRoute roles={['agent']}>
                    <AgendaPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/numeros"
                element={
                  <ProtectedRoute roles={['agent']}>
                    <NumbersPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/pre-call/:assignmentId" element={<PreCallPage />} />
              <Route path="/screening/:screeningId" element={<ScreeningPage />} />
              <Route path="/clients/:clientId" element={<ClientProfilePage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/settings/mfa/enroll" element={<MfaEnrollPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
